'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Play } from 'lucide-react';

import type { EntradaFuncionalidad } from '@/content/navegacionFuncionalidades';

type VideoFuncionalidad = NonNullable<EntradaFuncionalidad['video']>;
type EstadoReproductor = 'inactivo' | 'reproduciendo' | 'error';

interface PropiedadesVideoExplicativo {
    video?: VideoFuncionalidad;
    etiqueta: string;
}

const TEXTO_ERROR = 'No se ha podido cargar el vídeo. Tienes todo lo que cuenta en la transcripción de abajo.';

// m:ss: es un cronómetro, no un calendario, así que no hace falta ninguna librería de fechas
// para formatear la duración de un vídeo de segundos.
function formatearDuracion(duracionSegundos: number): string {
    const totalSegundos = Math.round(duracionSegundos);
    const minutos = Math.floor(totalSegundos / 60);
    const segundos = totalSegundos % 60;
    return `${minutos}:${String(segundos).padStart(2, '0')}`;
}

// El .vtt de subtítulos no es un campo más de la entrada: se deriva por convención del mismo
// nombre que el .mp4 (ver navegacionFuncionalidades.ts).
function rutaSubtitulos(rutaVideo: string): string {
    return rutaVideo.replace(/\.mp4$/, '.vtt');
}

// Un AbortError solo dice que otra orden (una pausa, un cambio de fuente) interrumpió el play();
// el vídeo sigue siendo reproducible, así que no merece el estado de error.
function esErrorDeReproduccion(error: unknown): boolean {
    return !(error instanceof DOMException && error.name === 'AbortError');
}

/**
 * Fachada de póster y reproductor de la sección de vídeo de funcionalidad (D-08, UI-SPEC §A.3–A.5).
 * Hasta el clic no se descarga ni un byte de vídeo ni de subtítulos.
 */
export default function VideoExplicativo({ video, etiqueta }: PropiedadesVideoExplicativo) {
    const [estado, setEstado] = useState<EstadoReproductor>('inactivo');
    const referenciaVideo = useRef<HTMLVideoElement>(null);

    // Arrancar, enfocar y activar los subtítulos vive en un efecto sobre el estado y no en el
    // manejador del clic (ni detrás del guard de `!video`: un hook no puede quedar tras un return
    // condicional). En el manejador, React aún no ha confirmado `controls`, un <video> sin controles
    // no es enfocable y el focus() síncrono no hacía nada mientras el botón, que tenía el foco, se
    // desmontaba en ese mismo render: el foco caía en <body> (bug real, visto con Playwright contra
    // el build de producción). El efecto corre DESPUÉS del commit, cuando el elemento ya es enfocable.
    useEffect(() => {
        if (estado !== 'reproduciendo') return;
        const elemento = referenciaVideo.current;
        if (!elemento) return;

        elemento.play()?.catch((error: unknown) => {
            if (esErrorDeReproduccion(error)) setEstado('error');
        });
        elemento.focus();

        // Los subtítulos se activan aquí, por script, y no con el atributo `default` del <track>: ese
        // atributo hace que el navegador pida el .vtt en cuanto se monta el <video>, mire o no
        // `preload`, y rompería la promesa de cero bytes hasta el clic. Sin `default` la pista nace
        // «disabled» (no se pide nada) y pasa a «showing» solo cuando el usuario ya ha pulsado.
        const pistaSubtitulos = elemento.textTracks[0];
        if (pistaSubtitulos) pistaSubtitulos.mode = 'showing';
    }, [estado]);

    // Sin vídeo en FUNCIONALIDADES_NAV no hay nada que enseñar: ni marcador ni «próximamente».
    if (!video) return null;

    const marcarError = () => setEstado('error');

    return (
        <div className="flex flex-col gap-2">
            <div
                className="relative overflow-hidden rounded-[20px] border-2 border-ink bg-surface-1 shadow-ds-2"
                style={{ aspectRatio: `${video.ancho} / ${video.alto}` }}
            >
                {/*
                 * El <video> se pinta SIEMPRE, también en el HTML del servidor, para que el buscador lo
                 * encuentre: un VideoObject sobre un <video> ausente invalida los datos estructurados.
                 * `preload="none"` lo deja en cero bytes hasta el clic. Sin atributo `poster`: la
                 * <Image> de abajo ya hace de póster optimizado, y declararlo aquí haría bajar también
                 * el JPG crudo. `tabIndex={-1}` lo deja enfocable por script sin meterlo en el orden de
                 * tabulación mientras el botón es el único control.
                 */}
                <video
                    ref={referenciaVideo}
                    className="video-marketing block h-full w-full"
                    width={video.ancho}
                    height={video.alto}
                    controls={estado === 'reproduciendo'}
                    playsInline
                    preload="none"
                    tabIndex={-1}
                    onError={marcarError}
                >
                    {/* Un fallo al cargar la fuente se dispara en el <source>, no en el <video>. */}
                    <source src={video.ruta} type="video/mp4" onError={marcarError} />
                    <track kind="captions" src={rutaSubtitulos(video.ruta)} srcLang="es" label="Español" />
                </video>

                {estado !== 'reproduciendo' && (
                    <Image
                        src={video.poster}
                        alt={`Vista previa del vídeo: ${etiqueta}`}
                        fill
                        sizes="(min-width: 1024px) 852px, 100vw"
                        loading="lazy"
                        className="object-cover"
                    />
                )}

                {estado === 'inactivo' && (
                    <BotonReproducir etiqueta={etiqueta} duracion={formatearDuracion(video.duracionSegundos)} alPulsar={() => setEstado('reproduciendo')} />
                )}
            </div>

            {estado === 'error' && (
                <p role="status" className="m-0 text-[15px] leading-[1.6] text-ink/90">
                    {TEXTO_ERROR}
                </p>
            )}
        </div>
    );
}

interface PropiedadesBotonReproducir {
    etiqueta: string;
    duracion: string;
    alPulsar: () => void;
}

// Todo el póster es el objetivo del clic. El anillo de foco global (offset 2) quedaría recortado por
// el overflow-hidden del marco, así que aquí se dibuja hacia dentro para que siga contorneando el marco.
function BotonReproducir({ etiqueta, duracion, alPulsar }: PropiedadesBotonReproducir) {
    return (
        <button
            type="button"
            onClick={alPulsar}
            aria-label={`Reproducir vídeo: ${etiqueta}`}
            className="group absolute inset-0 flex items-center justify-center focus-visible:outline-offset-[-6px]"
        >
            <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-ink bg-accent-blue text-on-accent-blue shadow-ds-2 transition-all duration-ds-base group-hover:shadow-ds-3 group-active:shadow-none motion-safe:group-hover:-translate-y-0.5 motion-safe:group-active:translate-y-0.5 lg:h-20 lg:w-20">
                <Play aria-hidden="true" className="ml-0.5 h-6 w-6 fill-current lg:h-8 lg:w-8" />
            </span>
            <span aria-hidden="true" className="absolute bottom-3 right-3 rounded-full bg-ink px-3 py-1 font-sans text-[13px] font-bold leading-[1.45] text-canvas tabular-nums">
                {duracion}
            </span>
        </button>
    );
}
